import { friendlyError } from "@/lib/friendly-errors";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Mail,
  MessageCircle,
  Clock,
  ShieldAlert,
  HelpCircle,
  LifeBuoy,
  Send,
  CheckCircle2,
  ExternalLink,
  Globe,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import {
  submitSupportRequest,
  getSupportEmailStatus,
} from "@/lib/support.functions";


const EXTERNAL_RESOURCES: {
  region: string;
  flag: string;
  items: { name: string; description: string; url: string }[];
}[] = [
  {
    region: "United States",
    flag: "🇺🇸",
    items: [
      {
        name: "NAMI (National Alliance on Mental Illness)",
        description:
          "One of the largest mental health organisations in the United States, providing education, support groups, advocacy, and resources for individuals and families.",
        url: "https://www.nami.org",
      },
      {
        name: "988 Suicide & Crisis Lifeline",
        description:
          "24/7 crisis support for people experiencing emotional distress, suicidal thoughts, or mental health crises.",
        url: "https://988lifeline.org",
      },
      {
        name: "SAMHSA",
        description:
          "Mental health and substance use resources, treatment locators, and educational information.",
        url: "https://www.samhsa.gov",
      },
    ],
  },
  {
    region: "United Kingdom",
    flag: "🇬🇧",
    items: [
      {
        name: "Mind",
        description:
          "Mental health information, support, helplines, and wellbeing resources.",
        url: "https://www.mind.org.uk",
      },
      {
        name: "NHS Mental Health Services",
        description:
          "Information about mental health conditions, treatment options, and support services.",
        url: "https://www.nhs.uk/mental-health/",
      },
      {
        name: "Samaritans",
        description: "24/7 emotional support for anyone struggling to cope.",
        url: "https://www.samaritans.org",
      },
    ],
  },
  {
    region: "Trauma Recovery",
    flag: "🌍",
    items: [
      {
        name: "Trauma Research Foundation",
        description:
          "Education and resources focused on trauma recovery and nervous system healing.",
        url: "https://traumaresearchfoundation.org",
      },
      {
        name: "Bessel van der Kolk Resources",
        description:
          "Educational resources from the author of The Body Keeps the Score.",
        url: "https://www.besselvanderkolk.com",
      },
    ],
  },
  {
    region: "Therapy Approaches",
    flag: "🧠",
    items: [
      {
        name: "IFS Institute",
        description:
          "Information about Internal Family Systems (IFS) therapy.",
        url: "https://ifs-institute.com",
      },
      {
        name: "EMDRIA",
        description:
          "Information about EMDR therapy and therapist directories.",
        url: "https://www.emdria.org",
      },
    ],
  },
];


export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "Contact Recovery Trauma™" },
      {
        name: "description",
        content:
          "Contact Recovery Trauma™ about your account, membership, events, therapist directory, payments, or technical issues. We normally reply within 1–2 business days.",
      },
      { property: "og:title", content: "Contact Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Send the Recovery Trauma™ team a message about account, membership, events, therapist directory, or technical questions.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/support" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/support" }],
  }),
  component: SupportPage,
});



function SupportPage() {
  const submit = useServerFn(submitSupportRequest);
  const fetchStatus = useServerFn(getSupportEmailStatus);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [delivery, setDelivery] = useState<{
    message_id: string;
    routed_to: string;
    status: "pending" | "sent" | "dlq" | "failed" | "suppressed" | "bounced" | "complained" | "unknown";
    error?: string | null;
    stopped?: boolean;
  } | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, []);

  const startPolling = (message_id: string) => {
    const started = Date.now();
    let notified = false;
    const tick = async () => {
      try {
        const r = await fetchStatus({ data: { message_id } });
        if (r.found) {
          const s = r.status as NonNullable<typeof delivery>["status"];
          setDelivery((d) => (d ? { ...d, status: s, error: r.error_message } : d));
          const terminal = ["sent", "dlq", "failed", "suppressed", "bounced", "complained"];
          if (terminal.includes(r.status) && !notified) {
            notified = true;
            if (r.status === "sent") {
              toast.success("Delivered to our support inbox ✓", {
                description: "Your message has arrived. We'll reply within 1–2 business days.",
              });
            } else {
              toast.error("We couldn't deliver your message", {
                description: "Please email info@recoverytrauma.com as a backup.",
              });
            }
            setDelivery((d) => (d ? { ...d, stopped: true } : d));
            return;
          }
        }
      } catch (e) {
        // network hiccup — keep polling until timeout
      }
      if (Date.now() - started < 90_000) {
        pollRef.current = window.setTimeout(tick, 2500);
      } else {
        setDelivery((d) => (d ? { ...d, stopped: true } : d));
      }
    };
    pollRef.current = window.setTimeout(tick, 1500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedEmail || !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error("Please enter a valid email address so we can reply.");
      return;
    }
    if (trimmedMessage.length < 5) {
      toast.error("Please add a short message (at least 5 characters).");
      return;
    }
    setSubmitting(true);
    setDelivery(null);
    try {
      const res = await submit({
        data: {
          name: name.trim() || null,
          email: trimmedEmail,
          message: trimmedMessage,
        },
      });
      setSent(true);
      setMessage("");
      setDelivery({
        message_id: res.message_id,
        routed_to: res.routed_to,
        status: "pending",
      });
      startPolling(res.message_id);
      toast.success("Message queued ✓", {
        description: "Confirming delivery to our support inbox…",
      });
    } catch (err) {
      const msg = friendlyError(err, "Something went wrong. Please try again or email info@recoverytrauma.com.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };




  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Mail className="h-3.5 w-3.5" /> 📧 Contact Support
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Contact Recovery Trauma
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          Have a question about your account, membership, events, therapist
          directory, payments, or technical issues? Send us a message and our
          team will normally respond within 1–2 business days. For quick
          answers, please check the{" "}
          <Link to="/faq" className="text-gold underline-offset-2 hover:underline">
            FAQ
          </Link>{" "}
          first.
        </p>
      </header>

      {/* Crisis notice */}
      <div className="mb-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="text-sm leading-relaxed text-foreground/85">
            <p className="font-semibold text-foreground">Need urgent help?</p>
            <p className="mt-1">
              Recovery Trauma™ is not a crisis service and cannot provide
              emergency assistance. If you are in immediate danger or need
              urgent mental health support, please contact local emergency
              services or visit our Crisis Resources page.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/crisis"
                className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-3.5 py-1.5 text-xs font-semibold text-destructive-foreground transition-opacity hover:opacity-90"
              >
                <LifeBuoy className="h-3.5 w-3.5" /> View Crisis Resources
              </Link>
              <Link
                to="/grounding"
                className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent"
              >
                Open Ground Me Tools
              </Link>
            </div>
          </div>
        </div>
      </div>

      <ComplianceNotice className="mb-8" />



      <div className="grid gap-6 md:grid-cols-[1.4fr_1fr]">
        {/* Contact form */}
        <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          {sent ? (
            <div className="py-10 text-center">
              {/* The headline only promises a reply once delivery to the
                  support inbox is actually confirmed. While it is pending, or
                  if it failed, the wording says so instead. */}
              {delivery && delivery.status === "pending" ? (
                <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-gold" />
              ) : (
                <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-gold" />
              )}
              <h2 className="font-serif text-xl text-foreground">
                {delivery && delivery.status === "pending"
                  ? "Sending your message…"
                  : "Thank you for contacting Recovery Trauma™"}
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                {delivery && delivery.status === "pending"
                  ? "Your message has been saved. We're confirming it reached our support inbox — this usually takes a few seconds."
                  : "We've received your message and will normally reply within 1–2 business days."}
              </p>

              {delivery && (
                <div
                  className={`mx-auto mt-5 max-w-sm rounded-xl border p-4 text-start ${
                    delivery.status === "sent"
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : ["dlq", "failed", "bounced", "complained", "suppressed"].includes(delivery.status)
                        ? "border-destructive/40 bg-destructive/5"
                        : "border-border bg-background"
                  }`}
                >
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Delivery status
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    {delivery.status === "pending" && (
                      <>
                        <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                        <span>Queued for {delivery.routed_to}. Confirming delivery…</span>
                      </>
                    )}
                    {delivery.status === "sent" && (
                      <>
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        <span>
                          Delivered to <strong>{delivery.routed_to}</strong>.
                        </span>
                      </>
                    )}
                    {["dlq", "failed", "bounced", "complained", "suppressed"].includes(delivery.status) && (
                      <div className="flex-1">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                          <div>
                            <p className="font-semibold text-destructive">
                              {delivery.status === "suppressed"
                                ? "Delivery blocked"
                                : "Delivery failed"}
                            </p>
                            <p className="mt-1 text-foreground/80">
                              We saved your message but couldn't deliver the notification to our support inbox. Please email us directly at{" "}
                              <a href="mailto:info@recoverytrauma.com" className="font-medium text-gold underline">
                                info@recoverytrauma.com
                              </a>{" "}
                              and quote the reference below so we can match it up.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {delivery.error && (
                    <p className="mt-2 rounded-md bg-background/60 p-2 text-xs text-muted-foreground">
                      Details: {delivery.error}
                    </p>
                  )}
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Reference: <code>{delivery.message_id}</code>
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setDelivery(null);
                }}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Send another message
              </button>
            </div>

          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="font-serif text-xl text-foreground">
                Send us a message
              </h2>


              <div>
                <label
                  htmlFor="name"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Your name <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="How should we address you?"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Your email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  We'll reply to this address.
                </p>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  How can we help?
                </label>
                <textarea
                  id="message"
                  required
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Share as little or as much as you'd like…"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--gold-deep)] disabled:opacity-50"
                disabled={submitting}

              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" /> Send to support
                  </>
                )}
              </button>
              <p className="text-center text-xs text-muted-foreground">
                Sent securely through Recovery Trauma™. We'll reply within 1–2 business days.
              </p>
            </form>
          )}
        </section>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-2 inline-flex items-center gap-2 text-foreground">
              <Mail className="h-4 w-4 text-gold" />
              <span className="text-sm font-semibold">Email us directly</span>
            </div>
            <div className="space-y-3">
              <div>
                <a
                  href="mailto:info@recoverytrauma.com"
                  className="block text-sm text-gold underline-offset-2 hover:underline"
                >
                  📧 info@recoverytrauma.com
                </a>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  General enquiries, partnerships & media
                </p>
              </div>
              <div>
                <a
                  href="mailto:support@recoverytrauma.com"
                  className="block text-sm text-gold underline-offset-2 hover:underline"
                >
                  🤍 support@recoverytrauma.com
                </a>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Member support & account assistance
                </p>
              </div>
              <div>
                <a
                  href="mailto:therapists@recoverytrauma.com"
                  className="block text-sm text-gold underline-offset-2 hover:underline"
                >
                  🩺 therapists@recoverytrauma.com
                </a>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Therapist applications & directory enquiries
                </p>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Best for attachments or longer messages.
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-2 inline-flex items-center gap-2 text-foreground">
              <Clock className="h-4 w-4 text-gold" />
              <span className="text-sm font-semibold">Response times</span>
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>Mon–Fri: within 24 hours</li>
              <li>Weekends &amp; holidays: next business day</li>
              <li>Billing issues: prioritised</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-2 inline-flex items-center gap-2 text-foreground">
              <HelpCircle className="h-4 w-4 text-gold" />
              <span className="text-sm font-semibold">Quick answers</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Most questions are answered instantly in our FAQ.
            </p>
            <Link
              to="/faq"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-gold underline-offset-2 hover:underline"
            >
              Browse FAQ →
            </Link>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-2 inline-flex items-center gap-2 text-foreground">
              <MessageCircle className="h-4 w-4 text-gold" />
              <span className="text-sm font-semibold">Community</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Members can also ask peers in our moderated community space.
            </p>
            <Link
              to="/community"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-gold underline-offset-2 hover:underline"
            >
              Visit community →
            </Link>
          </div>
        </aside>
      </div>

      {/* Trusted External Resources */}
      <section className="mt-12">
        <header className="mb-6 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <Globe className="h-3.5 w-3.5" /> 🌍 Trusted External Resources
          </div>
          <h2 className="font-serif text-3xl tracking-tight text-foreground">
            Reputable mental health & trauma recovery organisations
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            A curated list of trusted organisations offering mental health
            support, crisis services, trauma recovery education, and therapy
            information.
          </p>
        </header>

        <div className="space-y-8">
          {EXTERNAL_RESOURCES.map((group) => (
            <div key={group.region}>
              <h3 className="mb-3 flex items-center gap-2 font-serif text-xl text-foreground">
                <span aria-hidden>{group.flag}</span> {group.region}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {group.items.map((r) => (
                  <article
                    key={r.url}
                    className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm"
                  >
                    <h4 className="font-semibold text-foreground">{r.name}</h4>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {r.description}
                    </p>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center justify-center gap-1.5 self-start rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold text-foreground transition-colors hover:bg-accent"
                    >
                      Visit website <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 rounded-2xl border border-border bg-muted/30 p-4 text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">Disclaimer:</span>{" "}
          Recovery Trauma™ does not own, control, or endorse third-party
          websites and is not responsible for external content, services, or
          information provided by external organisations.
        </p>
      </section>
    </div>
  );
}
